import api from '../api';
import { sanitizeForLog } from '../../utilities/logSanitizer';

export interface ApplicationSettingsResponse {
  values: {
    ApplicationDeploymentMode: string;
    EnablePumpChecker: string;
    HostingEnvironment: string;
    EnableLicenseCheck: string;
    EnableUserAnalytics?: string;
    EnableLiveStreaming?: string;
  };
  id: string;
  dateCreated: string;
}

let settingsCache: ApplicationSettingsResponse | null = null;
let settingsPromise: Promise<ApplicationSettingsResponse | null> | null = null;

const fetchApplicationSettingsAsync = async (): Promise<ApplicationSettingsResponse | null> => {
  if (settingsCache) {
    return settingsCache;
  }

  if (settingsPromise) {
    return settingsPromise;
  }

  settingsPromise = new Promise<ApplicationSettingsResponse | null>((resolve) => {
    api
      .get<ApplicationSettingsResponse>('/Settings/GetApplicationSettings', {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then((response) => {
        if (response && response.data) {
          settingsCache = response.data;
          resolve(response.data);
        } else {
          const sanitizedResponse = sanitizeForLog(JSON.stringify(response));
          console.log('No data found in response', sanitizedResponse);
          resolve(null);
        }
      })
      .catch((error) => {
        console.error('Error fetching application settings:', error);
        resolve(null);
      })
      .finally(() => {
        settingsPromise = null;
      });
  });

  return settingsPromise;
};

const isUserAnalyticsEnabled = async (): Promise<boolean> => {
  const settings = await fetchApplicationSettingsAsync();
  return settings?.values?.EnableUserAnalytics === 'true';
};

const isLiveStreamingEnabled = async (): Promise<boolean> => {
  const settings = await fetchApplicationSettingsAsync();
  return settings?.values?.EnableLiveStreaming === 'true';
};

const ALLY_CONFIGURATIONS = {
  MAX_CARDS_SELECTABLE: 150,
  MAX_WELLS_SELECTABLE: 150,
  WELL_SCAN_TIMEOUT_MS: 1 * 60 * 1000, // 1 minute
};

export { fetchApplicationSettingsAsync, isUserAnalyticsEnabled, isLiveStreamingEnabled, ALLY_CONFIGURATIONS };
